import type {
  AdminOrgDetail,
  AdminOrgSummary,
  AdminUserDetail,
  AdminUserSummary,
  AiCreditPackageRecord,
  AiCreditPackageWrite,
  AiGlobalSettings,
  ApiError,
  BillingSummary,
  DataStatus,
  GrantRequest,
  OrgRole,
  Paginated,
  PlanDetail,
  PlanRecord,
  PlanUsageInfo,
  PlanWriteRequest,
  RefundRequest,
  SetPlanRequest,
  UsageAnalytics,
  AppUsageAnalytics,
  OrgTokenUsage,
  OrgTokenSeries,
} from "@currencycore/shared";
import {
  MOCK_ADMIN,
  MOCK_ENABLED,
  MOCK_SESSION_KEY,
  mockBanUser,
  mockDataStatus,
  mockGetAiSettings,
  mockSetAiSettings,
  mockSetOrgAi,
  mockSetOrgAuditLogs,
  mockDeleteUser,
  mockGetOrg,
  mockGetUser,
  mockListOrgs,
  mockListUsers,
  mockSetMemberRole,
  mockSetOrgPlan,
  mockAddPlanOrg,
  mockBilling,
  mockCopyPlan,
  mockCreatePlan,
  mockDeletePlan,
  mockGetPlan,
  mockGrant,
  mockListOrgPlans,
  mockListPlans,
  mockListAiCreditPackages,
  mockUpdateAiCreditPackage,
  mockRefund,
  mockRemovePlanOrg,
  mockSetOrgCap,
  mockUpdatePlan,
  mockSyncPpp,
  mockSyncRates,
  mockFetchRatesForDate,
  mockUnbanUser,
  mockUploadPpp,
  mockUploadRates,
  mockUsage,
  mockStatusSummary,
  mockListIncidents,
  mockCreateIncident,
  mockPostIncidentUpdate,
  mockDeleteIncident,
} from "./mock.ts";
import type {
  StatusResponse,
  IncidentDTO,
  IncidentImpact,
  IncidentStatus,
} from "@currencycore/shared";

const BASE_URL =
  (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ??
  "https://api.currency-core.com";

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError,
  ) {
    super(body.error.message);
    this.name = "ApiRequestError";
  }
  get details(): unknown {
    return this.body.error.details;
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({
      error: { code: "internal_error", message: res.statusText },
    }))) as ApiError;
    throw new ApiRequestError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function cleanParams(p: Record<string, string | number | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) {
    if (v !== undefined && v !== "") out[k] = String(v);
  }
  return out;
}

function qs(p: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams(cleanParams(p));
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// --- session / access -------------------------------------------------------

export interface AdminMe {
  authenticated: boolean;
  email: string | null;
  isAdmin: boolean;
}

export function getMe(): Promise<AdminMe> {
  if (MOCK_ENABLED) {
    const loggedIn = window.localStorage.getItem(MOCK_SESSION_KEY) === "1";
    return Promise.resolve({
      authenticated: loggedIn,
      email: loggedIn ? MOCK_ADMIN.email : null,
      isAdmin: loggedIn,
    });
  }
  return apiFetch<AdminMe>("/admin/me");
}

// --- data management --------------------------------------------------------

export interface RateSyncResult {
  ok: boolean;
  dates: string[];
  latestDate: string;
  currencyCount: number;
  rowsWritten: number;
  /** Dates persisted this run (always present; full list for the daily sync). */
  written: string[];
  /** Dates skipped because they already had data (only when backfilling). */
  skipped: string[];
}
export interface PppSyncResult {
  ok: boolean;
  fetched: number;
  eligible: number;
  changed: number;
  unchanged: number;
  years: { min: number; max: number } | null;
}

export function getDataStatus(): Promise<DataStatus> {
  if (MOCK_ENABLED) return Promise.resolve(mockDataStatus());
  return apiFetch<DataStatus>("/admin/data/status");
}
export function syncRates(): Promise<RateSyncResult> {
  if (MOCK_ENABLED) return Promise.resolve(mockSyncRates());
  return apiFetch<RateSyncResult>("/admin/sync", { method: "POST" });
}
/** Fetch & store rates for one specific (possibly past) date. */
export function fetchRatesForDate(date: string): Promise<RateSyncResult> {
  if (MOCK_ENABLED) return Promise.resolve(mockFetchRatesForDate(date));
  return apiFetch<RateSyncResult>("/admin/rates/fetch", {
    method: "POST",
    body: JSON.stringify({ date }),
  });
}
export function syncPpp(): Promise<PppSyncResult> {
  if (MOCK_ENABLED) return Promise.resolve(mockSyncPpp());
  return apiFetch<PppSyncResult>("/admin/sync-ppp", { method: "POST" });
}
/** Generate and cache homepage AI responses for all 6 canonical questions. */
export function generateHomepageAI(): Promise<{ ok: boolean; message: string }> {
  return apiFetch<{ ok: boolean; message: string }>("/admin/homepage/generate", { method: "POST" });
}
export function uploadPpp(payload: unknown): Promise<PppSyncResult> {
  if (MOCK_ENABLED) return Promise.resolve(mockUploadPpp());
  return apiFetch<PppSyncResult>("/admin/ppp/upload", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
/**
 * Upload a rates JSON (`currencycore-rates/v1`). By default it backfills only —
 * dates that already have data are skipped. `force=true` force-pushes, overwriting
 * existing dates with the values from the file.
 */
export function uploadRates(payload: unknown, force = false): Promise<RateSyncResult> {
  if (MOCK_ENABLED) return Promise.resolve(mockUploadRates(payload));
  return apiFetch<RateSyncResult>(`/admin/rates/upload${force ? "?force=true" : ""}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// --- organizations ----------------------------------------------------------

export interface ListParams {
  q?: string;
  plan?: string;
  status?: string;
  banned?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: string;
  [key: string]: string | number | undefined;
}

export function listOrgs(p: ListParams = {}): Promise<Paginated<AdminOrgSummary>> {
  if (MOCK_ENABLED) return Promise.resolve(mockListOrgs(new URLSearchParams(cleanParams(p))));
  return apiFetch<Paginated<AdminOrgSummary>>(`/admin/orgs${qs(p)}`);
}
export function getOrg(id: string): Promise<AdminOrgDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockGetOrg(id));
  return apiFetch<AdminOrgDetail>(`/admin/orgs/${id}`);
}
export function setOrgPlan(id: string, body: SetPlanRequest): Promise<AdminOrgDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockSetOrgPlan(id, body));
  return apiFetch<AdminOrgDetail>(`/admin/orgs/${id}/plan`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}
/** Schedule an org's downgrade to Hobby (free) at the end of its billing period. */
export function downgradeOrg(id: string): Promise<AdminOrgDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockSetOrgPlan(id, { planId: "free" }));
  return apiFetch<AdminOrgDetail>(`/admin/orgs/${id}/downgrade`, { method: "POST" });
}
export function setMemberRole(id: string, uid: string, role: OrgRole): Promise<AdminOrgDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockSetMemberRole(id, uid, role));
  return apiFetch<AdminOrgDetail>(`/admin/orgs/${id}/members/${uid}`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}
export function getOrgBilling(id: string): Promise<BillingSummary> {
  if (MOCK_ENABLED) return Promise.resolve(mockBilling(id));
  return apiFetch<BillingSummary>(`/admin/orgs/${id}/billing`);
}
/** Initiate a Dodo refund for one paid invoice; returns the refreshed billing. */
export function refundInvoice(id: string, body: RefundRequest): Promise<BillingSummary> {
  if (MOCK_ENABLED) return Promise.resolve(mockRefund(id, body));
  return apiFetch<BillingSummary>(`/admin/orgs/${id}/refund`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
export function grantLimit(id: string, body: GrantRequest): Promise<AdminOrgDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockGrant(id, body));
  return apiFetch<AdminOrgDetail>(`/admin/orgs/${id}/grant`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
/** Set/clear the admin ceiling and the org's own hard usage cap. */
export function setOrgCap(
  id: string,
  body: { adminRequests?: number | null; orgRequests?: number | null },
): Promise<AdminOrgDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockSetOrgCap(id, body));
  return apiFetch<AdminOrgDetail>(`/admin/orgs/${id}/cap`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// --- AI controls ------------------------------------------------------------

export function getAiSettings(): Promise<AiGlobalSettings> {
  if (MOCK_ENABLED) return Promise.resolve(mockGetAiSettings());
  return apiFetch<AiGlobalSettings>("/admin/settings/ai");
}

export function setAiSettings(body: AiGlobalSettings): Promise<AiGlobalSettings> {
  if (MOCK_ENABLED) return Promise.resolve(mockSetAiSettings(body));
  return apiFetch<AiGlobalSettings>("/admin/settings/ai", { method: "PUT", body: JSON.stringify(body) });
}

/** Admin enable/disable the AI endpoint for a single org. */
export function setOrgAi(id: string, enabled: boolean): Promise<AdminOrgDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockSetOrgAi(id, enabled));
  return apiFetch<AdminOrgDetail>(`/admin/orgs/${id}/ai`, { method: "PATCH", body: JSON.stringify({ enabled }) });
}

/** Override the customer audit-log entitlement: true=on, false=off, null=inherit from plan. */
export function setOrgAuditLogs(id: string, override: boolean | null): Promise<AdminOrgDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockSetOrgAuditLogs(id, override));
  return apiFetch<AdminOrgDetail>(`/admin/orgs/${id}/audit-logs`, { method: "PATCH", body: JSON.stringify({ override }) });
}
// SSO (enterprise) — capability override + Require-SSO + providers.
export interface AdminSsoProvider {
  id: string;
  providerId: string;
  domain: string;
  issuer: string;
  domainVerified: boolean;
}
export interface AdminSsoStatus {
  plan: string;
  ssoEnabledOverride: boolean;
  enabled: boolean;
  enforced: boolean;
  providers: AdminSsoProvider[];
}
export function getOrgSso(id: string): Promise<AdminSsoStatus> {
  if (MOCK_ENABLED) {
    return Promise.resolve({ plan: "enterprise", ssoEnabledOverride: false, enabled: true, enforced: false, providers: [] });
  }
  return apiFetch<AdminSsoStatus>(`/admin/orgs/${id}/sso`);
}
export function patchOrgSso(
  id: string,
  body: { ssoEnabled?: boolean; ssoEnforced?: boolean },
): Promise<{ ok: boolean }> {
  if (MOCK_ENABLED) return Promise.resolve({ ok: true });
  return apiFetch<{ ok: boolean }>(`/admin/orgs/${id}/sso`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}
export function deleteSsoProvider(id: string, providerId: string): Promise<{ ok: boolean }> {
  if (MOCK_ENABLED) return Promise.resolve({ ok: true });
  return apiFetch<{ ok: boolean }>(`/admin/orgs/${id}/sso/providers/${providerId}`, { method: "DELETE" });
}

/** Plans assignable to a specific org (standard + this org's allow-listed custom plans). */
export function listOrgPlans(id: string): Promise<PlanUsageInfo[]> {
  if (MOCK_ENABLED) return Promise.resolve(mockListOrgPlans(id));
  return apiFetch<{ items: PlanUsageInfo[] }>(`/admin/orgs/${id}/plans`).then((r) => r.items);
}

// --- plans ------------------------------------------------------------------

export function listPlans(): Promise<PlanUsageInfo[]> {
  if (MOCK_ENABLED) return Promise.resolve(mockListPlans());
  return apiFetch<{ items: PlanUsageInfo[] }>("/admin/plans").then((r) => r.items);
}
export function getPlan(id: string): Promise<PlanDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockGetPlan(id));
  return apiFetch<PlanDetail>(`/admin/plans/${id}`);
}
export function addPlanOrg(planId: string, organizationId: string): Promise<PlanDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockAddPlanOrg(planId, organizationId));
  return apiFetch<PlanDetail>(`/admin/plans/${planId}/orgs`, {
    method: "POST",
    body: JSON.stringify({ organizationId }),
  });
}
export function removePlanOrg(planId: string, orgId: string): Promise<PlanDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockRemovePlanOrg(planId, orgId));
  return apiFetch<PlanDetail>(`/admin/plans/${planId}/orgs/${orgId}`, { method: "DELETE" });
}
export function createPlan(body: PlanWriteRequest): Promise<PlanRecord> {
  if (MOCK_ENABLED) return Promise.resolve(mockCreatePlan(body));
  return apiFetch<PlanRecord>("/admin/plans", { method: "POST", body: JSON.stringify(body) });
}
export function copyPlan(id: string): Promise<PlanRecord> {
  if (MOCK_ENABLED) return Promise.resolve(mockCopyPlan(id));
  return apiFetch<PlanRecord>(`/admin/plans/${id}/copy`, { method: "POST" });
}
export function updatePlan(id: string, body: PlanWriteRequest): Promise<PlanRecord> {
  if (MOCK_ENABLED) return Promise.resolve(mockUpdatePlan(id, body));
  return apiFetch<PlanRecord>(`/admin/plans/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}
export function deletePlan(id: string): Promise<{ ok: boolean }> {
  if (MOCK_ENABLED) return Promise.resolve(mockDeletePlan(id));
  return apiFetch<{ ok: boolean }>(`/admin/plans/${id}`, { method: "DELETE" });
}

// --- AI credit packages -----------------------------------------------------

export function listAiCreditPackages(): Promise<{ items: AiCreditPackageRecord[] }> {
  if (MOCK_ENABLED) return Promise.resolve({ items: mockListAiCreditPackages() });
  return apiFetch<{ items: AiCreditPackageRecord[] }>("/admin/ai-credits/packages");
}
export function updateAiCreditPackage(
  id: string,
  body: AiCreditPackageWrite,
): Promise<AiCreditPackageRecord> {
  if (MOCK_ENABLED) return Promise.resolve(mockUpdateAiCreditPackage(id, body));
  return apiFetch<AiCreditPackageRecord>(`/admin/ai-credits/packages/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// --- users ------------------------------------------------------------------

export function listUsers(p: ListParams = {}): Promise<Paginated<AdminUserSummary>> {
  if (MOCK_ENABLED) return Promise.resolve(mockListUsers(new URLSearchParams(cleanParams(p))));
  return apiFetch<Paginated<AdminUserSummary>>(`/admin/users${qs(p)}`);
}
export function getUser(id: string): Promise<AdminUserDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockGetUser(id));
  return apiFetch<AdminUserDetail>(`/admin/users/${id}`);
}
export function banUser(id: string, reason: string): Promise<AdminUserDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockBanUser(id, reason || null));
  return apiFetch<AdminUserDetail>(`/admin/users/${id}/ban`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}
export function unbanUser(id: string): Promise<AdminUserDetail> {
  if (MOCK_ENABLED) return Promise.resolve(mockUnbanUser(id));
  return apiFetch<AdminUserDetail>(`/admin/users/${id}/unban`, { method: "POST" });
}

export interface DeleteUserResult {
  ok: boolean;
  deletedOrgs: { id: string; name: string }[];
}
export function deleteUser(id: string): Promise<DeleteUserResult> {
  if (MOCK_ENABLED) {
    try {
      return Promise.resolve(mockDeleteUser(id));
    } catch (e) {
      const err = e as Error & { organizations?: { id: string; name: string }[] };
      return Promise.reject(
        new ApiRequestError(409, {
          error: {
            code: "invalid_input",
            message: err.message,
            details: { organizations: err.organizations ?? [] },
          },
        }),
      );
    }
  }
  return apiFetch<DeleteUserResult>(`/admin/users/${id}`, { method: "DELETE" });
}

// --- usage analytics --------------------------------------------------------

export interface UsageParams {
  from?: string;
  to?: string;
  granularity?: string;
  org?: string;
  plan?: string;
  [key: string]: string | undefined;
}
export function getUsage(p: UsageParams = {}): Promise<UsageAnalytics> {
  if (MOCK_ENABLED) return Promise.resolve(mockUsage(new URLSearchParams(cleanParams(p))));
  return apiFetch<UsageAnalytics>(`/admin/usage${qs(p)}`);
}

const ZERO_TOKENS = { inputTokens: 0, outputTokens: 0, totalTokens: 0 };
const ZERO_ENDPOINTS = { convert: 0, rates: 0, ppp: 0, ai: 0 };

/** App-wide AI token analytics (totals, endpoint counts, monthly chart, top orgs). */
export function getTokenUsage(): Promise<AppUsageAnalytics> {
  if (MOCK_ENABLED) {
    return Promise.resolve({
      totalTokens: ZERO_TOKENS,
      monthTokens: ZERO_TOKENS,
      avgMonthlyTokens: ZERO_TOKENS,
      monthsActive: 0,
      endpointTotal: ZERO_ENDPOINTS,
      endpointMonth: ZERO_ENDPOINTS,
      monthly: [],
      topOrgs: [],
    });
  }
  return apiFetch<AppUsageAnalytics>("/admin/usage/tokens");
}

/** One org's AI token card + monthly token series. */
export function getOrgTokens(id: string): Promise<{ usage: OrgTokenUsage; series: OrgTokenSeries }> {
  if (MOCK_ENABLED) {
    return Promise.resolve({
      usage: { organizationId: id, name: "", total: ZERO_TOKENS, month: ZERO_TOKENS, avgMonthly: ZERO_TOKENS, monthsActive: 0 },
      series: { organizationId: id, months: [] },
    });
  }
  return apiFetch<{ usage: OrgTokenUsage; series: OrgTokenSeries }>(`/admin/orgs/${id}/tokens`);
}

// --- Status / incidents -----------------------------------------------------

export function getStatusSummary(): Promise<StatusResponse> {
  if (MOCK_ENABLED) return Promise.resolve(mockStatusSummary());
  return apiFetch<StatusResponse>("/admin/status/summary");
}

export function listIncidents(): Promise<{ incidents: IncidentDTO[] }> {
  if (MOCK_ENABLED) return Promise.resolve({ incidents: mockListIncidents() });
  return apiFetch<{ incidents: IncidentDTO[] }>("/admin/status/incidents");
}

export function createIncident(body: {
  title: string;
  impact: IncidentImpact;
  status: IncidentStatus;
  body: string;
}): Promise<{ ok: boolean; id: string }> {
  if (MOCK_ENABLED) return Promise.resolve(mockCreateIncident(body));
  return apiFetch<{ ok: boolean; id: string }>("/admin/status/incidents", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function postIncidentUpdate(
  id: string,
  body: { status: IncidentStatus; body: string },
): Promise<{ ok: boolean }> {
  if (MOCK_ENABLED) return Promise.resolve(mockPostIncidentUpdate(id, body));
  return apiFetch<{ ok: boolean }>(`/admin/status/incidents/${id}/updates`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteIncident(id: string): Promise<{ ok: boolean }> {
  if (MOCK_ENABLED) return Promise.resolve(mockDeleteIncident(id));
  return apiFetch<{ ok: boolean }>(`/admin/status/incidents/${id}`, { method: "DELETE" });
}

// ---------- AI query log ----------

export interface AiQueryLogRow {
  id: number;
  created_at: string;
  org_id: string | null;
  plan_id: string | null;
  status: "success" | "unsupported" | "timeout" | "unavailable" | "error";
  failure_reason: string | null;
  intent: string | null;
  query: string;
  answer: string | null;
  tool_calls: number | null;
  total_tool_calls: number | null;
  tool_call_sequence: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  total_tokens: number | null;
  estimated_tokens: number | null;
  model: string | null;
  provider: string | null;
  provider_kind: "native" | "byok" | "cloudflare" | null;
  byok: number | null;
  neurons: number | null;
  history_allowed: number | null;
  execution_ms: number | null;
}

export interface AiQueryLogParams {
  limit?: number;
  offset?: number;
  status?: string;
  org_id?: string;
  q?: string;
}

export function getAiQueries(p: AiQueryLogParams = {}): Promise<{
  items: AiQueryLogRow[];
  total: number;
  limit: number;
  offset: number;
}> {
  return apiFetch(`/admin/ai-queries${qs(cleanParams({ ...p, limit: p.limit ?? 50, offset: p.offset ?? 0 }))}`);
}

// ---------- Outbound email ----------

export { sendOutreachEmail, type SendEmailPayload, type SendEmailResult } from "./email-service.ts";

